import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EngineDashboard } from '@/components/EngineDashboard';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import {
  fetchSeoAudits,
  fetchSeoProjectOptions,
  runSeoAudit,
  type SeoAuditRow,
  type SeoAuditType,
} from '@/data/sources';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { formatRelative } from '@/lib/utils';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { resolveActiveProjectId } from '@/pages/seo/domain-scope';
import { DomainChoiceGate } from '@/pages/seo/DomainChoiceGate';

/* /seo/site-health — stored site-health results per project
 * (api/seo.yaml: GET/POST /seo/projects/{id}/audits). Evidence-first honesty:
 * every card renders ONLY what the stored provider payload carries, with its
 * source attribution; a crux_field result with no_data:true renders an explicit
 * "Google has no field data for this origin" state — never blank, never
 * invented. Runs are freshness-cached server-side: a 200 cached:true response
 * is surfaced as a quiet "served from cache" note with a Run fresh (force)
 * escape hatch; 429 rate_limited / 402 budget_exceeded / 502 provider_error /
 * 503 provider_unavailable all render as honest human guidance. */

const AUDIT_TYPES: Array<{ key: SeoAuditType; label: string; paid: boolean }> = [
  { key: 'psi_lab', label: 'Lab performance (PSI)', paid: false },
  { key: 'crux_field', label: 'Field data (CrUX)', paid: false },
  { key: 'onpage', label: 'On-page crawl', paid: true },
];

/* Site Health subnav presets (G1/B3 IA regroup) — each subnav entry maps onto
 * an EXISTING audit type; no new data surfaces, just a focused view. Technical
 * Issues = the onpage crawl, which keeps its existing honest awaiting-provider
 * (503) state when the provider isn't connected. */
const PRESETS: Record<SeoAuditType, { title: string; subtitle: string }> = {
  psi_lab: {
    title: 'PageSpeed',
    subtitle:
      'Lab performance audits from Google PageSpeed Insights — stored results, source-attributed, exactly as captured.',
  },
  crux_field: {
    title: 'Core Web Vitals',
    subtitle:
      'Field data from the Chrome UX Report (p75 LCP · INP · CLS) — real-visitor metrics; an origin without enough traffic shows Google’s explicit no-data answer.',
  },
  onpage: {
    title: 'Technical Issues',
    subtitle:
      'On-page crawl results from DataForSEO — crawl errors and issues found. Crawls are paid and budget-guarded; if the provider isn’t connected yet, a run says so honestly.',
  },
};

function auditTypeLabel(key: string): string {
  return AUDIT_TYPES.find((t) => t.key === key)?.label ?? key;
}

function humanizeKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** Nested objects whose scalar entries are summary metrics worth surfacing. */
const NESTED_METRIC_KEYS = ['scores', 'categories', 'metrics', 'p75', 'summary'];
const SKIP_KEYS = new Set(['url', 'origin', 'no_data', 'source', 'cached']);

/** Pull the numeric summary metrics out of a stored result payload — only what
 *  is actually there; an empty list falls back to the raw-payload disclosure. */
function extractMetrics(result: Record<string, unknown>): Array<{ label: string; value: string }> {
  const metrics: Array<{ label: string; value: string }> = [];
  const add = (key: string, value: unknown, prefix?: string) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return;
    metrics.push({
      label: `${prefix ? `${prefix} · ` : ''}${humanizeKey(key)}`,
      value: Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100),
    });
  };
  for (const [key, value] of Object.entries(result)) {
    if (SKIP_KEYS.has(key)) continue;
    if (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      NESTED_METRIC_KEYS.includes(key)
    ) {
      const prefix = key === 'p75' ? 'p75' : undefined;
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) add(k, v, prefix);
    } else {
      add(key, value);
    }
  }
  return metrics;
}

interface InlinePanel {
  title: string;
  body: string;
  link?: { to: string; label: string };
}

function AuditCard({ audit }: { audit: SeoAuditRow }) {
  const noData = audit.result.no_data === true;
  const subject =
    typeof audit.result.url === 'string'
      ? audit.result.url
      : typeof audit.result.origin === 'string'
        ? audit.result.origin
        : null;
  const metrics = noData ? [] : extractMetrics(audit.result);
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="engine">{auditTypeLabel(audit.audit_type)}</Badge>
        {subject && <span className="text-sm font-medium text-text-primary">{subject}</span>}
        <span className="ml-auto text-xs text-text-tertiary">
          {formatRelative(audit.captured_at)} · source: {audit.source}
        </span>
      </div>

      {noData ? (
        <p className="mt-3 text-sm text-text-secondary">
          Google has no field data for this origin — the Chrome UX Report only publishes metrics
          once an origin has enough real-visitor traffic. This is the provider’s real answer,
          stored as an explicit no-data result, not an error and not missing data.
        </p>
      ) : metrics.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
          {metrics.map((m) => (
            <div key={m.label}>
              <div className="text-[11px] uppercase tracking-wide text-text-tertiary">{m.label}</div>
              <div className="mt-0.5 font-mono text-sm font-semibold text-text-primary" data-tabular>
                {m.value}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-sm text-text-secondary">
          This stored result carries no scalar summary metrics — the raw provider payload is below,
          exactly as captured.
        </p>
      )}

      <details className="mt-3">
        <summary className="cursor-pointer text-xs text-text-tertiary hover:text-text-secondary">
          View stored payload
        </summary>
        <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-inset p-3 text-[11px] leading-relaxed text-text-secondary">
          {JSON.stringify(audit.result, null, 2)}
        </pre>
      </details>
    </Card>
  );
}

export function SeoSiteHealthPage({ preset }: { preset?: SeoAuditType } = {}) {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Subnav preset (PageSpeed / Core Web Vitals / Technical Issues) pins the
  // view to one existing audit type; the router keys each preset route so the
  // component remounts with fresh state when the subnav changes.
  const presetMeta = preset ? PRESETS[preset] : null;
  const pageTitle = presetMeta?.title ?? 'Site Health';

  const [filter, setFilter] = useState<'all' | SeoAuditType>(preset ?? 'all');
  const [runType, setRunType] = useState<SeoAuditType>(preset ?? 'psi_lab');
  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [panel, setPanel] = useState<InlinePanel | null>(null);
  const [cachedNote, setCachedNote] = useState<{ capturedAt: string | null; noData: boolean } | null>(
    null,
  );

  const projectsQ = useQuery({
    queryKey: ['seo', 'project-options'],
    queryFn: fetchSeoProjectOptions,
  });
  const projects = projectsQ.data ?? [];
  const { active, select } = useActiveDomain();
  const projectId = resolveActiveProjectId(active, projects);
  const project = projects.find((p) => p.id === projectId) ?? null;

  const auditsQ = useQuery({
    queryKey: ['seo', 'audits', projectId],
    queryFn: () => fetchSeoAudits(projectId as string),
    enabled: projectId !== null,
  });

  const clearFeedback = () => {
    setNotice(null);
    setPanel(null);
    setCachedNote(null);
  };

  const handleRun = async (force: boolean) => {
    if (projectId === null || running) return;
    clearFeedback();
    setRunning(true);
    try {
      const res = await runSeoAudit(projectId, { audit_type: runType, force });
      if (res.cached) {
        // HTTP 200 — a fresh-enough stored result was served; no provider call.
        setCachedNote({ capturedAt: res.captured_at, noData: res.no_data });
      } else {
        // HTTP 201 — fresh provider run stored.
        setNotice(
          res.no_data
            ? `Field-data check stored — Google has no field data for this origin (source: ${res.source}). That’s the provider’s real answer, kept as an explicit no-data result.`
            : `Fresh ${auditTypeLabel(res.audit_type)} audit stored (source: ${res.source}).`,
        );
        await queryClient.invalidateQueries({ queryKey: ['seo', 'audits', projectId] });
      }
    } catch (err) {
      if (err instanceof ApiError && (err.status === 429 || err.code === 'rate_limited')) {
        setPanel({
          title: 'Provider run limit reached for today',
          body: 'This workspace has reached its 10 provider audit runs per 24 hours, so nothing was run and nothing was stored. Cached results still serve for free below, and runs resume as the 24-hour window rolls over.',
        });
      } else if (err instanceof ApiError && (err.status === 402 || err.code === 'budget_exceeded')) {
        setPanel({
          title: 'Monthly provider budget reached',
          body: 'This on-page crawl would exceed your workspace’s monthly provider budget, so it was refused before any provider call was made — nothing was spent. Crawls resume when the budget resets next month.',
          link: { to: '/seo/spend', label: 'Review spend & budget →' },
        });
      } else if (err instanceof ApiError && (err.status === 502 || err.code === 'provider_error')) {
        setPanel({
          title: 'The provider request failed',
          body: `${err.message ? `${err.message} ` : ''}Nothing was stored — previously stored results below are unaffected. Try again in a moment.`,
        });
      } else if (
        err instanceof ApiError &&
        (err.status === 503 || err.code === 'provider_unavailable')
      ) {
        setPanel({
          title: 'Site-health audits aren’t available yet',
          body: 'Live audits need the provider connections (Google PageSpeed Insights, Chrome UX Report, DataForSEO), which aren’t configured for this environment yet. Nothing was charged — previously stored results still show below.',
        });
      } else {
        setPanel({
          title: 'The audit could not be completed',
          body:
            err instanceof ApiError && err.message
              ? `${err.message} No result was stored — try again.`
              : 'The data service returned an unexpected response. No result was stored — try again.',
        });
      }
    } finally {
      setRunning(false);
    }
  };

  if (projectsQ.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title={pageTitle} />
        <LoadingState rows={4} />
      </div>
    );
  }
  if (projectsQ.error) {
    return (
      <div className="space-y-6">
        <PageHeader title={pageTitle} />
        <ErrorState error={projectsQ.error} onRetry={() => void projectsQ.refetch()} />
      </div>
    );
  }

  if (projects.length > 0 && projectId === null) {
    return <DomainChoiceGate options={projects} onSelect={(id) => void select(Number(id))} />;
  }
  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title={pageTitle} />
        <EmptyState
          title="No domains to audit yet"
          teach="Site-health audits live inside an SEO project. Add your first domain on the Projects page, then come back here to run lab, field and on-page checks against it."
          action={
            <Button variant="primary" onClick={() => navigate('/seo/projects')}>
              Go to Projects
            </Button>
          }
        />
      </div>
    );
  }

  const audits = auditsQ.data ?? [];
  const visible = filter === 'all' ? audits : audits.filter((a) => a.audit_type === filter);

  const projectSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Domain</span>
      <select
        value={projectId ?? ''}
        onChange={(e) => {
          void select(Number(e.target.value));
          clearFeedback();
        }}
        className="h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30"
        aria-label="SEO project"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );

  const runControls = canManage ? (
    <div className="flex items-center gap-2">
      <select
        value={runType}
        onChange={(e) => setRunType(e.target.value as SeoAuditType)}
        disabled={running}
        className="h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30"
        aria-label="Audit type to run"
      >
        {AUDIT_TYPES.map((t) => (
          <option key={t.key} value={t.key}>
            {t.label}
            {t.paid ? ' — paid' : ''}
          </option>
        ))}
      </select>
      <Button
        variant="primary"
        disabled={running}
        onClick={() => void handleRun(false)}
        title="PSI runs can take 30–60 seconds. Fresh-enough stored results are served from cache without a provider call; on-page crawls are priced up-front against the monthly budget."
      >
        {running ? 'Running… (can take up to a minute)' : 'Run audit'}
      </Button>
    </div>
  ) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={pageTitle}
        subtitle={presetMeta?.subtitle}
        actions={
          <>
            {projectSelector}
            {runControls}
          </>
        }
      />

      {notice && (
        <div className="rounded-md border border-success/20 bg-success/10 px-3 py-2 text-sm text-success">
          {notice}
        </div>
      )}

      {cachedNote && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border-subtle bg-inset/50 px-3 py-2"
        >
          <p className="text-sm text-text-secondary">
            Served from cache — a fresh-enough result was already stored, captured{' '}
            {cachedNote.capturedAt ? formatRelative(cachedNote.capturedAt) : 'recently'}. No
            provider call was made.
            {cachedNote.noData &&
              ' That stored result is an explicit “Google has no field data for this origin” outcome.'}
          </p>
          {canManage && (
            <Button
              variant="secondary"
              disabled={running}
              onClick={() => void handleRun(true)}
              title="Bypasses the freshness cache — the fresh run still counts against the 10-per-24h provider cap"
            >
              Run fresh
            </Button>
          )}
        </div>
      )}

      {panel && (
        <div role="status" className="rounded-md border border-border-default bg-surface px-4 py-3">
          <div className="text-sm font-medium text-text-primary">{panel.title}</div>
          <p className="mt-1 text-sm text-text-secondary">{panel.body}</p>
          {panel.link && (
            <Link
              to={panel.link.to}
              className="mt-2 inline-block text-sm font-medium text-engine hover:underline"
            >
              {panel.link.label}
            </Link>
          )}
        </div>
      )}

      <EngineDashboard
        engineName="SEO Engine"
        heroLabel={
          project
            ? `${presetMeta ? presetMeta.title : 'Site health'} — ${project.label}`
            : (presetMeta?.title ?? 'Site health')
        }
        hero={<span data-tabular>{auditsQ.isSuccess ? visible.length : '—'}</span>}
        heroSub="Stored audit results: lab performance from Google PageSpeed Insights, field data from the Chrome UX Report, on-page crawls from DataForSEO — every result is source-attributed, exactly as captured."
      >
        {/* the preset subnav pins the audit type, so the manual filter only
            shows on the combined Site Health view */}
        {!preset && (
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2">
              <span className="text-xs font-medium text-text-secondary">Show</span>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as 'all' | SeoAuditType)}
                className="h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30"
                aria-label="Filter by audit type"
              >
                <option value="all">All audit types</option>
                {AUDIT_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {auditsQ.isPending ? (
          <LoadingState rows={4} />
        ) : auditsQ.error ? (
          <ErrorState error={auditsQ.error} onRetry={() => void auditsQ.refetch()} />
        ) : visible.length === 0 ? (
          <EmptyState
            title={filter === 'all' ? 'No audits stored yet' : 'No audits of this type yet'}
            teach={`Run a lab performance, field data or on-page audit against ${project?.domain ?? 'this domain'} — results are stored with their source, and fresh-enough results are served from cache for free.`}
            action={
              canManage ? (
                <Button variant="primary" disabled={running} onClick={() => void handleRun(false)}>
                  {running ? 'Running…' : 'Run audit'}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="space-y-3">
            {visible.map((a) => (
              <AuditCard key={a.id} audit={a} />
            ))}
          </div>
        )}
      </EngineDashboard>
    </div>
  );
}
